import type {
  PrayerPreferencesDto,
  UpdatePrayerPreferencesRequest,
} from '@/types'
import { api } from '@/services/api'
import { isNativePlatform } from '@/utils/platform'

export const PRAYER_PREFS_STORAGE_KEY = 'dhikr_prayer_prefs_v1'

export type LocalPrayerPreferences = Omit<
  PrayerPreferencesDto,
  'id' | 'userId' | 'createdAt' | 'updatedAt'
>

export function createDefaultPrayerPreferences(): LocalPrayerPreferences {
  return {
    prayerNotificationsEnabled: true,
    fajrEnabled: true,
    dhuhrEnabled: true,
    asrEnabled: true,
    maghribEnabled: true,
    ishaEnabled: true,
    minutesBefore: 15,
    calculationMethod: 3,
    latitude: null,
    longitude: null,
    includeNearbyMosques: true,
    nearbyMosqueRadiusKm: 2,
    useLocalPrayerNotifications: isNativePlatform,
  }
}

function readLocalPrayerPreferences(): LocalPrayerPreferences {
  try {
    const raw = localStorage.getItem(PRAYER_PREFS_STORAGE_KEY)
    if (!raw) return createDefaultPrayerPreferences()
    return { ...createDefaultPrayerPreferences(), ...JSON.parse(raw) as Partial<LocalPrayerPreferences> }
  } catch {
    return createDefaultPrayerPreferences()
  }
}

function writeLocalPrayerPreferences(prefs: LocalPrayerPreferences): void {
  localStorage.setItem(PRAYER_PREFS_STORAGE_KEY, JSON.stringify(prefs))
}

function toUpdateRequest(
  prefs: LocalPrayerPreferences,
  lat: number,
  lng: number,
): UpdatePrayerPreferencesRequest {
  return {
    prayerNotificationsEnabled: prefs.prayerNotificationsEnabled,
    fajrEnabled: prefs.fajrEnabled,
    dhuhrEnabled: prefs.dhuhrEnabled,
    asrEnabled: prefs.asrEnabled,
    maghribEnabled: prefs.maghribEnabled,
    ishaEnabled: prefs.ishaEnabled,
    minutesBefore: prefs.minutesBefore,
    calculationMethod: prefs.calculationMethod,
    latitude: lat,
    longitude: lng,
    includeNearbyMosques: prefs.includeNearbyMosques,
    nearbyMosqueRadiusKm: prefs.nearbyMosqueRadiusKm,
    useLocalPrayerNotifications: isNativePlatform ? true : prefs.useLocalPrayerNotifications,
  }
}

export async function loadPrayerPreferences(isAuthenticated: boolean): Promise<LocalPrayerPreferences> {
  const local = readLocalPrayerPreferences()

  if (!isAuthenticated) {
    return local
  }

  try {
    const { data } = await api.getPrayerPreferences()
    if (data.success && data.data) {
      const server = data.data
      const merged: LocalPrayerPreferences = {
        prayerNotificationsEnabled: server.prayerNotificationsEnabled,
        fajrEnabled: server.fajrEnabled,
        dhuhrEnabled: server.dhuhrEnabled,
        asrEnabled: server.asrEnabled,
        maghribEnabled: server.maghribEnabled,
        ishaEnabled: server.ishaEnabled,
        minutesBefore: server.minutesBefore,
        calculationMethod: server.calculationMethod,
        latitude: server.latitude,
        longitude: server.longitude,
        includeNearbyMosques: server.includeNearbyMosques,
        nearbyMosqueRadiusKm: server.nearbyMosqueRadiusKm,
        useLocalPrayerNotifications: isNativePlatform || server.useLocalPrayerNotifications,
      }
      writeLocalPrayerPreferences(merged)
      return merged
    }
  } catch {
    // Fall back to local copy when offline or unauthorized.
  }

  return local
}

export async function savePrayerPreferences(
  prefs: LocalPrayerPreferences,
  lat: number,
  lng: number,
  isAuthenticated: boolean,
): Promise<LocalPrayerPreferences> {
  const normalized: LocalPrayerPreferences = {
    ...prefs,
    latitude: lat,
    longitude: lng,
    useLocalPrayerNotifications: isNativePlatform ? true : prefs.useLocalPrayerNotifications,
  }

  writeLocalPrayerPreferences(normalized)

  if (isAuthenticated) {
    const { data } = await api.updatePrayerPreferences(toUpdateRequest(normalized, lat, lng))
    if (data.success && data.data) {
      const saved: LocalPrayerPreferences = {
        prayerNotificationsEnabled: data.data.prayerNotificationsEnabled,
        fajrEnabled: data.data.fajrEnabled,
        dhuhrEnabled: data.data.dhuhrEnabled,
        asrEnabled: data.data.asrEnabled,
        maghribEnabled: data.data.maghribEnabled,
        ishaEnabled: data.data.ishaEnabled,
        minutesBefore: data.data.minutesBefore,
        calculationMethod: data.data.calculationMethod,
        latitude: data.data.latitude,
        longitude: data.data.longitude,
        includeNearbyMosques: data.data.includeNearbyMosques,
        nearbyMosqueRadiusKm: data.data.nearbyMosqueRadiusKm,
        useLocalPrayerNotifications: data.data.useLocalPrayerNotifications,
      }
      writeLocalPrayerPreferences(saved)
      return saved
    }
  }

  return normalized
}

export async function mergeGuestPrefsToServer(lat: number, lng: number): Promise<void> {
  const local = readLocalPrayerPreferences()
  await api.updatePrayerPreferences(toUpdateRequest(local, lat, lng))
}

export function prayerPreferencesFingerprint(prefs: LocalPrayerPreferences): string {
  return JSON.stringify({
    prayerNotificationsEnabled: prefs.prayerNotificationsEnabled,
    fajrEnabled: prefs.fajrEnabled,
    dhuhrEnabled: prefs.dhuhrEnabled,
    asrEnabled: prefs.asrEnabled,
    maghribEnabled: prefs.maghribEnabled,
    ishaEnabled: prefs.ishaEnabled,
    minutesBefore: prefs.minutesBefore,
    calculationMethod: prefs.calculationMethod,
    includeNearbyMosques: prefs.includeNearbyMosques,
    nearbyMosqueRadiusKm: prefs.nearbyMosqueRadiusKm,
  })
}
